import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { api } from '@/api/client'
import { Footer } from '@/components/Footer'
import { PublicHeader } from '@/components/PublicHeader'

interface UnsubscribePage {
  address: string
  already_unsubscribed: boolean
  campaign: string
  note: string
}

/**
 * Unsubscribe, in two steps on purpose.
 *
 * Reading this page changes nothing; the button is what acts. That is not
 * politeness — this URL goes out in an email, and corporate mail scanners
 * fetch every link in every message they inspect. A page that unsubscribed on
 * load would let a scanner silently stop somebody's reminders, and it would
 * look exactly like a delivery problem.
 */
export function Unsubscribe() {
  const { token = '' } = useParams()

  const page = useQuery({
    queryKey: ['unsubscribe', token],
    queryFn: () => api.get<UnsubscribePage>(`/api/unsubscribe/${token}`),
  })

  const stop = useMutation({
    mutationFn: (scope: 'campaign' | 'all') =>
      api.post(`/api/unsubscribe/${token}?scope=${scope}`),
  })

  const done = stop.isSuccess || page.data?.already_unsubscribed
  const mutedOne = stop.isSuccess && stop.variables === 'campaign'

  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        alignContent: 'center',
        minHeight: '100dvh',
        p: 2,
      }}
    >
      <Paper sx={{ p: 3.5, maxWidth: 400 }}>
        <PublicHeader />
        <Typography variant="h2" sx={{ mb: 1 }}>
          {mutedOne ? 'Muted' : done ? 'Unsubscribed' : 'Stop these emails?'}
        </Typography>

        {page.error && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            That link does not work any more.
          </Typography>
        )}

        {page.data && (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {mutedOne
                ? `We will not email ${page.data.address} about ${page.data.campaign} again. Everything else carries on.`
                : done
                ? `We will not email ${page.data.address} again.`
                : `Which emails should stop going to ${page.data.address}?`}
            </Typography>

            <Alert severity="info" sx={{ mb: 2.5, fontSize: '0.8125rem' }}>
              {page.data.note}
            </Alert>

            {/* Two buttons, and the smaller one first.
              *
              * Somebody clicking Unsubscribe in a message about one campaign
              * usually means THAT campaign. Offering only the global switch
              * made the commoner intent unavailable, so people either took
              * the nuclear option or replied to the manager asking them to
              * stop — which nothing in the system can act on.
              *
              * The global one stays plainly visible rather than hidden
              * behind the other: an opt-out that is hard to find is the
              * violation, whatever the wording says. */}
            {!done && (
              <Stack spacing={1}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={stop.isPending}
                  onClick={() => stop.mutate('campaign')}
                >
                  Just stop {page.data.campaign}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={stop.isPending}
                  onClick={() => stop.mutate('all')}
                >
                  Stop all email from Fronted
                </Button>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Either way you still owe what you owe — this only stops the
                  messages.
                </Typography>
              </Stack>
            )}
          </>
        )}
      </Paper>

      <Box sx={{ maxWidth: 400 }}>
        <Footer />
      </Box>
    </Box>
  )
}
